import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { Menu, X } from 'lucide-react';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_hotel-reserve-57/artifacts/fb0g6q0u_logo-yeni-amorium-copy.png';

const LANGUAGES = [
  { code: 'tr', label: 'TR', flag: '🇹🇷' },
  { code: 'en', label: 'EN', flag: '🇬🇧' },
];

export default function Navbar({ transparent = false }) {
  const { language, setLanguage, t } = useApp();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [isDesktop, setIsDesktop] = useState(window.innerWidth >= 768);
  const [actionBarOpen, setActionBarOpen] = useState(() => {
    const stored = localStorage.getItem('actionBarOpen');
    return stored === null ? true : stored === 'true';
  });

  useEffect(() => {
    const handler = () => setIsDesktop(window.innerWidth >= 768);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);

  // Listen for ActionBar toggle events
  useEffect(() => {
    const handleActionBarToggle = (e) => {
      setActionBarOpen(e.detail.isOpen);
    };
    window.addEventListener('actionBarToggle', handleActionBarToggle);
    return () => window.removeEventListener('actionBarToggle', handleActionBarToggle);
  }, []);

  // Close mobile menu on escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') setMobileOpen(false);
    };
    if (mobileOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = '';
    };
  }, [mobileOpen]);

  const navLinks = [
    { key: 'home', href: '/' },
    { key: 'rooms', href: '/rooms' },
    { label: language === 'tr' ? 'Rezervasyonumu Sorgula' : 'My Booking', href: '/my-booking' },
  ];

  const textColor = transparent ? '#FFFFFF' : '#1F3A2E';
  const borderStyle = transparent ? '1px solid rgba(255,255,255,0.35)' : '1px solid #D8D1C6';
  
  // Calculate top position based on ActionBar state
  const navbarTop = transparent ? 0 : (actionBarOpen ? 80 : 40);

  return (
    <>
      <nav data-testid="navbar" style={{
        position: transparent ? 'absolute' : 'sticky',
        top: navbarTop,
        left: 0, right: 0, zIndex: transparent ? 50 : 109,
        background: transparent ? 'transparent' : 'rgba(246, 241, 231, 0.97)',
        borderBottom: transparent ? 'none' : '1px solid #D8D1C6',
        backdropFilter: transparent ? 'none' : 'blur(8px)',
        transition: 'top 0.3s ease',
      }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 1.5rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', height: '64px' }}>

            {/* Logo */}
            <Link to="/" style={{ display: 'flex', alignItems: 'center', textDecoration: 'none' }}>
              {transparent ? (
                <span style={{ fontFamily: 'Cormorant Garamond, Georgia, serif', fontSize: '20px', fontWeight: 600, color: '#FFFFFF', textShadow: '0 1px 4px rgba(0,0,0,0.5)' }}>
                  Amorium Hotel
                </span>
              ) : (
                <img src={LOGO_URL} alt="Amorium Hotel" style={{ height: '40px', width: 'auto', objectFit: 'contain', maxWidth: '150px' }} />
              )}
            </Link>

            {/* Desktop Nav Links */}
            {isDesktop && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '24px' }}>
                {navLinks.map(link => (
                  <Link key={link.href || link.key} to={link.href || '/'} style={{ fontSize: '14px', fontWeight: 500, color: textColor, textDecoration: 'none' }}>
                    {link.label || t(link.key)}
                  </Link>
                ))}
              </div>
            )}

            {/* Desktop Controls */}
            {isDesktop && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                {/* Language Flags - side by side, both always visible */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '3px', background: transparent ? 'rgba(255,255,255,0.1)' : '#EFE7DA', borderRadius: '24px', padding: '3px', border: borderStyle }}>
                  {LANGUAGES.map(lang => (
                    <button
                      key={lang.code}
                      data-testid="top-nav-language-toggle"
                      onClick={() => setLanguage(lang.code)}
                      style={{
                        display: 'inline-flex', alignItems: 'center', gap: '4px',
                        padding: '4px 10px', borderRadius: '20px', border: 'none',
                        cursor: 'pointer', fontSize: '12px', fontWeight: 700,
                        fontFamily: 'Manrope, sans-serif',
                        background: language === lang.code
                          ? (transparent ? 'rgba(255,255,255,0.25)' : '#1F3A2E')
                          : 'transparent',
                        color: language === lang.code
                          ? 'white'
                          : (transparent ? 'rgba(255,255,255,0.75)' : '#6E7F5B'),
                        transition: 'background 0.15s, color 0.15s',
                      }}
                    >
                      <span style={{ fontSize: '14px' }}>{lang.flag}</span>
                      {lang.label}
                    </button>
                  ))}
                </div>

                {/* Book Now */}
                <button onClick={() => navigate('/rooms')} style={{
                  background: transparent ? 'rgba(255,255,255,0.15)' : '#1F3A2E',
                  color: '#FFFFFF', fontSize: '13px', fontWeight: 600,
                  padding: '7px 16px', borderRadius: '8px', marginLeft: '8px',
                  border: transparent ? '1px solid rgba(255,255,255,0.3)' : 'none',
                  cursor: 'pointer', fontFamily: 'Manrope, sans-serif',
                }}
                  onMouseEnter={e => !transparent && (e.currentTarget.style.background = '#3E5A3F')}
                  onMouseLeave={e => !transparent && (e.currentTarget.style.background = '#1F3A2E')}
                >
                  {t('book_now')}
                </button>
              </div>
            )}

            {/* Mobile Hamburger Button */}
            {!isDesktop && (
              <button
                onClick={() => setMobileOpen(!mobileOpen)}
                aria-label="Menu"
                style={{
                  padding: '8px',
                  background: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  color: textColor,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  zIndex: 1001,
                }}
              >
                {mobileOpen ? <X size={24} /> : <Menu size={24} />}
              </button>
            )}
          </div>
        </div>
      </nav>

      {/* Mobile Menu Overlay */}
      {!isDesktop && mobileOpen && (
        <>
          {/* Backdrop */}
          <div
            onClick={() => setMobileOpen(false)}
            style={{
              position: 'fixed',
              inset: 0,
              background: 'rgba(0,0,0,0.5)',
              zIndex: 999,
              animation: 'fadeIn 0.2s ease-in-out',
            }}
          />
          
          {/* Mobile Menu Panel */}
          <div
            style={{
              position: 'fixed',
              top: 0,
              right: 0,
              bottom: 0,
              width: '280px',
              maxWidth: '85vw',
              background: '#F6F1E7',
              zIndex: 1000,
              padding: '80px 24px 24px',
              overflowY: 'auto',
              boxShadow: '-4px 0 24px rgba(0,0,0,0.15)',
              animation: 'slideInRight 0.3s ease-out',
            }}
          >
            <img src={LOGO_URL} alt="Amorium Hotel" style={{ height: '36px', width: 'auto', marginBottom: '24px' }} />
            
            {navLinks.map(link => (
              <Link
                key={link.href || link.key}
                to={link.href || '/'}
                onClick={() => setMobileOpen(false)}
                style={{
                  display: 'block',
                  fontSize: '16px',
                  color: '#1F3A2E',
                  fontWeight: 500,
                  padding: '12px 0',
                  borderBottom: '1px solid #D8D1C6',
                  textDecoration: 'none',
                  fontFamily: 'Manrope, sans-serif',
                }}
              >
                {link.label || t(link.key)}
              </Link>
            ))}

            {/* Language Selection */}
            <div style={{ marginTop: '24px' }}>
              <p style={{ fontSize: '11px', color: '#6E7F5B', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '12px' }}>
                Dil / Language
              </p>
              <div style={{ display: 'flex', gap: '8px' }}>
                {LANGUAGES.map(lang => (
                  <button
                    key={lang.code}
                    onClick={() => {
                      setLanguage(lang.code);
                      setMobileOpen(false);
                    }}
                    style={{
                      flex: 1,
                      padding: '10px 12px',
                      borderRadius: '10px',
                      border: language === lang.code ? '2px solid #1F3A2E' : '1.5px solid #D8D1C6',
                      background: language === lang.code ? '#1F3A2E' : 'white',
                      color: language === lang.code ? 'white' : '#1F3A2E',
                      fontSize: '14px',
                      fontWeight: 700,
                      cursor: 'pointer',
                      fontFamily: 'Manrope, sans-serif',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '6px',
                    }}
                  >
                    <span style={{ fontSize: '18px' }}>{lang.flag}</span> {lang.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Book Now Button */}
            <button
              onClick={() => {
                navigate('/rooms');
                setMobileOpen(false);
              }}
              style={{
                width: '100%',
                background: '#1F3A2E',
                color: 'white',
                marginTop: '24px',
                padding: '14px',
                borderRadius: '12px',
                fontWeight: 600,
                border: 'none',
                cursor: 'pointer',
                fontFamily: 'Manrope, sans-serif',
                fontSize: '15px',
              }}
            >
              {t('book_now')}
            </button>
          </div>
        </>
      )}

      {/* CSS Animations */}
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes slideInRight {
          from {
            transform: translateX(100%);
          }
          to {
            transform: translateX(0);
          }
        }
      `}</style>
    </>
  );
}
